<template lang="pug">
form.leasing__form(@submit.prevent="submit")
	p.sf-text.leasing__form-header Kalkulátorunkkal kiszámolhatja, hogy KKV-nak minősülő vállalkozása számára milyen konkrét pénzügyi előnyöket nyújt egy személygépjármű lízingelése az egyösszegű készpénzes vásárláshoz képest.
		VueTooltip(placement="right")
			p(slot="content")
				| KKV meghatározása: a kis- és középvállalkozásokról, fejlődésük támogatásáról szóló 2004. évi XXXIV. törvény szerint
	.form-row
		.form-col
			label(for="lc-price") Bruttó vételár
		.form-col
			VueCurrencyInput#lc-price(v-model="model.price", aria-describedby="lc-price-err" postfix="Ft", :maxLength="11", :has-error="hasError('price')", @blur="validateField('price')")
			VueErrorMessage#lc-price-err(v-if="hasError('price')") {{ getErrorMessage('price') }}
	.form-row
		.form-col
			label(for="lc-tax") Regisztrációs adó(opcionális)
		.form-col
			VueCurrencyInput#lc-tax(v-model="model.tax", aria-describedby="lc-tax-err", postfix="Ft", :maxLength="7", :has-error="hasError('tax')", @blur="validateField('tax')")
			VueErrorMessage#lc-tax-err(v-if="hasError('tax')") {{ getErrorMessage('tax') }}
	.form-row
		.form-col
			label(for="lc-capital") Bruttó önerő
		.form-col
			VueCurrencyInput#lc-capital(v-model="model.capital", aria-describedby="lc-capital-err", postfix="Ft", :addon="`Minimum: ${MIN_OWN_CONTRIBUTION_PERCENT}% ${minOwnContribution ? `(${formatCurrency(minOwnContribution)})` : ''}`", :maxLength="11", :has-error="hasError('capital')", @blur="validateField('capital')")
			VueErrorMessage#lc-capital-err(v-if="hasError('capital')") {{ getErrorMessage('capital') }}
	.form-row
		.form-col
			label(for="lc-term") Futamidő
		.form-col.selectable-percent-value.leasing__form-slider
			VueSlider#lc-term(v-model="model.term", :min="TERM_MIN", :max="TERM_MAX", :step="1", :hide-limits="false", mobile-view="slider")
			span.selectable-percent-info {{ model.term }} hónap
	.form-row
		.form-col
			label(for="lc-residualPercentage") Nettó maradványérték nyíltvégű lízing esetén
				VueTooltip(placement="bottom")
					p(slot="content")
						| A nettó maradványérték a nettó vételár %-ban kerül meghatározásra. Nyíltvégű lízing esetén a gépjárművet a futamidő végén a maradványérték megfizetésével vásárolhatja meg. Minimum és maximum értéke függ a gépjármű bruttó vételárától és a választott futamidő hosszától.
		.form-col.leasing__form-slider.leasing-form-slider--middle-price
			VueSlider#lc-residualPercentage(v-model="model.residualPercentage", :min="residualLimits.residualMinPercentage", :max="residualLimits.residualMaxPercentage", :step="1", :hide-limits="false", mobile-view="slider", :bottom-text="netResidual ? formatCurrency(Math.round(netResidual)) : null")
			p.sf-text.form-col__residual-percentage.selectable-percent-info {{ model.residualPercentage }} %
	.form-row
		.form-col
			label(for="lc-interestRate") Ügyleti kamatláb (a lízing kalkuláció alapján adja meg)
		.form-col
			VueCurrencyInput#lc-interestRate(v-model="model.interestRate", aria-describedby="lc-interestRate-err", :max-width="128", postfix="%", :decimals="2", :has-error="hasError('interestRate')", @blur="validateField('interestRate')")
			VueErrorMessage#lc-interestRate-err(v-if="hasError('interestRate')") {{ getErrorMessage('interestRate') }}
	.form-row
		.form-col
			.label-tooltip-wrapper
				label(for="lc-businessUsagePercentage") Üzleti használat aránya
				VueTooltip(placement="bottom")
					p(slot="content")
						| Tételes útnyilvántartás nélkül a havi lízingdíj áfájának 50%-a vonható le igazolt üzleti használat esetén.
		.form-col
			VueCurrencyInput#lc-businessUsagePercentage(v-model="model.businessUsagePercentage", aria-describedby="lc-businessUsagePercentage-err", :max-width="104", postfix="%", :maxLength="3", :has-error="hasError('businessUsagePercentage')", @blur="validateField('businessUsagePercentage')")
			VueErrorMessage#lc-businessUsagePercentage-err(v-if="hasError('businessUsagePercentage')") {{ getErrorMessage('businessUsagePercentage') }}
	.form-row
		.form-col
			.label-tooltip-wrapper
				label(for="lc-yieldPercentage") Likviditási többlet hozama
				VueTooltip(placement="bottom")
					p(slot="content")
						| Készpénzes vásárlással szemben pénzügyi lízing finanszírozás esetén a cégben megmaradó tőke a cég tevékenységéből adódóan vagy más befektetésnek köszönhetően hozamot generál. Ennek a futamidő alatti teljes összegét tartalmazza a számítás.
		.form-col
			VueCurrencyInput#lc-yieldPercentage(v-model="model.yieldPercentage", aria-describedby="lc-yieldPercentage-err", :max-width="104", postfix="%", :maxLength="2", :has-error="hasError('yieldPercentage')", @blur="validateField('yieldPercentage')")
			VueErrorMessage#lc-yieldPercentage-err(v-if="hasError('yieldPercentage')") {{ getErrorMessage('yieldPercentage') }}
	.form-row(v-if="captchaImg")
		.form-col
			label(for="lc-captcha-text") Ellenőrző kód
		.form-col
			VueCaptcha#lc-captcha-text(v-model="model.captchaText", :max-length="5", :img="`data:image/jpeg;base64,${captchaImg}`", :has-error="hasError('captchaText')", :aria-describedby="hasError('captchaText') ? 'id' : null", :max-width="104", @keyup="resetCaptchaError", @blur="validateCaptcha")
			VueErrorMessage#lc-captcha-text-err(v-if="hasError('captchaText')") {{ getErrorMessage('captchaText') }}
	.form-row
		VueButton(ref="button", type="submit", :disabled="sending") Kiszámolom

</template>

<script setup>
import { computed, onMounted, reactive, ref, getCurrentInstance, watch, nextTick } from 'vue';
import leasingComparatorApi from '../../../api/leasing-comparator-api';
import { useStore } from '../store';
import { required, helpers } from '@vuelidate/validators';
import { useJumpToFirstError } from '../../../common/js/composables/jump-to-first-error';
import { useVuelidate } from '@vuelidate/core';
import { useErrorHandlerUtils } from '../../../common/js/composables/error-handler';
import { useCaptcha } from '../../../common/js/composables/captcha';
import { useConstants } from '../composables/constants';
import VueCurrencyInput from '@ui-common/vue-currency-input';
import VueTooltip from '@ui-common/vue-tooltip';
import VueButton from '@ui-common/vue-button';
import VueSlider from '@ui-common/vue-slider';
import VueErrorMessage from '@ui-common/vue-error-message';
import VueCaptcha from '@ui-common/vue-captcha';

const emit = defineEmits(['error']);

const formatCurrency = value => {
	if (isNaN(parseInt(value))) {
		return '-';
	}
	return new Intl.NumberFormat('hu', { style: 'currency', currency: 'HUF', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};

const {
	MIN_OWN_CONTRIBUTION_PERCENT,
	TERM_MIN,
	TERM_MAX,
	RESIDUAL_MINMAX_TABLE_LESS,
	RESIDUAL_MINMAX_TABLE_MORE,
	RESIDUAL_LIMIT,
	BUSINESS_USAGE_LIMITS,
	YIELD_LIMITS,
	MIN_CONTRIBUTION_PERCENTAGE,
	NET_PRICE_DIVISOR
} = useConstants();
const { model, result } = useStore();
const { captchaError, technicalError, captchaImg, captchaKey, loading, getCaptcha, resetCaptchaError } = useCaptcha(leasingComparatorApi.generateCaptcha);

onMounted(async () => {
	await getCaptcha();
});

const minOwnContribution = computed(() => model.value.price * (MIN_CONTRIBUTION_PERCENTAGE / 100));
const netPrice = computed(() => (model.value.price - model.value.tax) / NET_PRICE_DIVISOR);
const residualLimits = computed(() => {
	const table = netPrice.value < RESIDUAL_LIMIT ? RESIDUAL_MINMAX_TABLE_LESS : RESIDUAL_MINMAX_TABLE_MORE;
	for (const key in table) {
		const row = table[key];
		if (row.minTerm <= model.value.term && model.value.term <= row.maxTerm) {
			return row;
		}
	}
});
const netResidual = computed(() => (netPrice.value + model.value.tax) * (model.value.residualPercentage / 100));

watch(
	[() => residualLimits.value.residualMinPercentage, () => residualLimits.value.residualMaxPercentage],
	([min, max]) => {
		if (model.value.residualPercentage < min || model.value.residualPercentage > max) {
			model.value.residualPercentage = min;
		}
	},
	{ immediate: true }
);

const rules = computed(() => ({
	price: {
		required: helpers.withMessage('A bruttó vételár megadása kötelező.', required)
	},
	capital: {
		required: helpers.withMessage('A bruttó önerő megadása kötelező.', required),
		betweenMinAndPrice: helpers.withMessage(
			'A beírt adatok nem megfelelőek!',
			() => minOwnContribution.value <= model.value.capital && model.value.capital < model.value.price
		),
		lessThanResidual: helpers.withMessage(
			'Kérem, csökkentse az önerő mértékét vagy a maradványértéket.',
			() => model.value.price - model.value.capital > netResidual.value
		)
	},
	businessUsagePercentage: {
		required: helpers.withMessage('A üzleti használat arányának megadása kötelező.', required),
		smallerThan: helpers.withMessage(
			`A üzleti használat aránya nem lehet kevesebb mint ${BUSINESS_USAGE_LIMITS.MIN}%!`,
			() => model.value.businessUsagePercentage >= BUSINESS_USAGE_LIMITS.MIN
		),
		greaterThan: helpers.withMessage(
			`A üzleti használat aránya nem lehet több mint ${BUSINESS_USAGE_LIMITS.MAX}%!`,
			() => model.value.businessUsagePercentage <= BUSINESS_USAGE_LIMITS.MAX
		)
	},
	yieldPercentage: {
		required: helpers.withMessage('A likviditás többlet hozamának megadása kötelező.', required),
		smallerThan: helpers.withMessage(
			`A megadott likviditás többlet hozama nem lehet kevesebb mint ${YIELD_LIMITS.MIN}%!`,
			() => model.value.yieldPercentage >= YIELD_LIMITS.MIN
		),
		greaterThan: helpers.withMessage(
			`A megadott likviditás többlet hozama nem lehet több mint ${YIELD_LIMITS.MAX}%!`,
			() => model.value.yieldPercentage <= YIELD_LIMITS.MAX
		)
	},
	captchaText: {
		required: captchaImg.value ? helpers.withMessage('A kód megadása kötelező.', required) : {},
		captcha: captchaImg.value ? helpers.withMessage('A kód nem megfelelő.', () => captchaError.value === false) : {}
	}
}));
const v = useVuelidate(rules, model);
const { hasError, validateField, getErrorMessage } = useErrorHandlerUtils(v);
const { jumpToFirstError } = useJumpToFirstError(getCurrentInstance());

const button = ref(null);
const validateCaptcha = event => {
	if (event.relatedTarget != button.value.$el) {
		validateField('captchaText');
	}
};

const sending = ref(false);
const submit = async () => {
	v.value.$touch();
	if (v.value.$invalid) {
		jumpToFirstError();
		return;
	}
	sending.value = true;
	const sentModel = { ...model.value };
	sentModel.captchaKey = captchaKey.value;
	sentModel.interestRate ??= 0;
	try {
		const { data } = await leasingComparatorApi.submit(sentModel);
		result.value = data;
		result.value.calculatedModel = sentModel;
	} catch (error) {
		if (error?.response?.data?.error === 'captchaText') {
			captchaError.value = true;
		} else {
			emit('error');
		}
		return;
	} finally {
		sending.value = false;
	}
	model.value.captchaText = null;
	v.value.captchaText.$reset();
	await getCaptcha();
};

watch(technicalError, val => {
	if (val) emit('error');
});
</script>

<style src="@ui-common/vue-slider/dist/vue-slider.css"></style>
<style lang="scss" scoped>
@import '@ui-common/sf-style-utils/lib/helpers/_queries.scss';

.leasing__form-header {
	margin-bottom: 32px;

	@include media('<tablet') {
		margin: 0 16px 16px;
	}
}

.form-row {
	@include media('<tablet') {
		margin-bottom: 24px;
	}
}

.form-row .form-col:first-of-type {
	@include media('>=desktop') {
		max-width: 296px;
	}
}

.sf-slider {
	width: 256px;
}

.leasing__form-slider {
	align-items: center;
	display: flex;
}

.leasing-form-slider--middle-price {
	padding-bottom: 32px;
	position: relative;
}

.form-col__residual-percentage {
	width: 48px;
}

.selectable-percent-info {
	margin-bottom: 24px;
	margin-left: 32px;
}

.leasing-form-slider--middle-price :deep(.sf-slider__limits) {
	position: relative;
}

.leasing-form-slider--middle-price :deep(.sf-slider__limits)::after {
	bottom: -24px;
	font-weight: 700;
	left: 0;
	position: absolute;
	right: 0;
	text-align: center;
}

:deep(.sf-currency-input__wrapper) {
	max-width: 176px;

	@include media('<tablet') {
		max-width: 100%;
	}
}

.sf-btn {
	@include media('<tablet') {
		width: 100%;
	}
}

:deep(.sf-slider__value) {
	text-wrap: nowrap;
}
</style>
